// Pure logic for the printable operations report — no DOM, no fetch.
//
// The Report page (pages/report.ts) packages what the console already computes
// into a formal document. Everything here is the part of that packaging that
// can be tested without a browser: the report reference, mapping a requested
// time window onto a forecast's hourly axis, grading the window on its worst
// hour, and the formal-register sentences that extend the window briefing.
//
// Every number in every sentence is computed from data passed in; nothing is
// invented. The one set of literals — the forecast-skill citation — is quoted
// from published verification (Campos et al. 2024, Weather and Forecasting,
// DOI 10.1175/WAF-D-24-0043.1) rather than estimated.

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const HOUR_MS = 3_600_000;

const pad = (n: number): string => String(n).padStart(2, "0");

/** The duration presets the report form offers, in hours. */
export const DURATIONS: readonly number[] = [6, 12, 24, 48];

/**
 * The duration options to offer, given a length carried in from outside — the
 * console's work window, or a previously chosen value being restored. The
 * presets always appear; a carried length that is not one of them is inserted
 * in order rather than silently snapped to the nearest preset, because a form
 * prefilled from the console must show the console's own number.
 */
export function durationChoices(carriedHours: number | null): number[] {
  const out = [...DURATIONS];
  if (carriedHours !== null && Number.isFinite(carriedHours)) {
    const h = Math.round(carriedHours);
    if (h > 0 && !out.includes(h)) {
      out.push(h);
      out.sort((x, y) => x - y);
    }
  }
  return out;
}

/**
 * An instant as a datetime-local input value ("2026-08-12T18:00"), rounded to
 * the nearest hour, in UTC or in this device's clock — the two clocks the form
 * reads in (the site's zone is unknown until a forecast is fetched). Minutes
 * are kept rather than forced to :00 so half-hour zones stay truthful.
 */
export function startInputValue(ms: number, useUtc: boolean): string {
  const t = Math.round(ms / HOUR_MS) * HOUR_MS;
  const d = new Date(t);
  if (useUtc) {
    return (
      `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` +
      `T${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
    );
  }
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** A chart label ("2026-07-11T14:00", site wall clock) as a true UTC epoch —
 *  the same reconstruction computeNowIndex uses. */
export function epochOfLabel(label: string, utcOffsetSeconds: number): number {
  return Date.parse(label + ":00Z") - utcOffsetSeconds * 1000;
}

/** The document reference, from the generation instant: "SS-20260812-1430".
 *  UTC on purpose — a reference that moves with the reader's clock is two
 *  references. Minute precision: one report per minute is enough uniqueness
 *  for a document a person triggers by hand. */
export function reportRef(d: Date): string {
  return (
    `SS-${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
    `-${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}`
  );
}

/** The generated-at stamp: "2026-08-12 19:32 UTC". UTC only — the document
 *  prints site AND UTC for every forecast hour, but its own provenance wants
 *  the one clock every recipient shares. */
export function fmtGenerated(d: Date): string {
  return (
    `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}` +
    ` ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`
  );
}

/** Degrees + decimal minutes with hemisphere letters: "27°30.0′N 94°12.0′W".
 *  The surveyor's spelling — the decimal pair prints beside it, so the two
 *  cover both of the ways a position gets read back over a radio. */
export function fmtDM(lat: number, lon: number): string {
  const one = (v: number, pos: string, neg: string): string => {
    const abs = Math.abs(v);
    let deg = Math.floor(abs);
    let min = (abs - deg) * 60;
    // 59.96′ would print as "60.0′" — carry into the degree instead.
    if (min >= 59.95) {
      deg += 1;
      min = 0;
    }
    return `${deg}°${min.toFixed(1)}′${v >= 0 ? pos : neg}`;
  };
  return `${one(lat, "N", "S")} ${one(lon, "E", "W")}`;
}

/**
 * An instant in BOTH clocks: "Wed 12 Aug 14:00 (19:00 UTC)".
 *
 * The console lets the operator choose Local or UTC; a formal document that a
 * client forwards onward cannot know its reader's preference, so it always
 * prints the site's wall clock first (the console's stated convention — the
 * timeline reads in the clock of the water being worked) with UTC beside it.
 * The UTC half repeats the weekday only when it differs — "Wed 23:00 (Thu
 * 04:00 UTC)" names the day boundary instead of hiding it.
 */
export function fmtSiteUtc(t: number, utcOffsetSeconds: number): string {
  return fmtClock(t, utcOffsetSeconds, "both");
}

/**
 * Which clock a generated report prints its hours in.
 *
 * "both" stays the default and the recommendation, for the reason above. The
 * two single-clock modes exist because a document has a known destination often
 * enough to be worth the choice: a DPR filled in on the bridge wants the site's
 * wall clock and nothing else, and a report attached to a message passing
 * between offices in different countries wants UTC and nothing else. Both
 * reductions are honest — the mode is named on the face of the document — where
 * a report that silently dropped one would not be.
 */
export type ReportClock = "site" | "utc" | "both";

/** An instant in the report's chosen clock. */
export function fmtClock(t: number, utcOffsetSeconds: number, mode: ReportClock): string {
  const site = new Date(t + utcOffsetSeconds * 1000);
  const utc = new Date(t);
  const siteTxt =
    `${WEEKDAYS[site.getUTCDay()]} ${site.getUTCDate()} ${MONTHS[site.getUTCMonth()]}` +
    ` ${pad(site.getUTCHours())}:${pad(site.getUTCMinutes())}`;
  if (mode === "site") return siteTxt;
  const utcFull =
    `${WEEKDAYS[utc.getUTCDay()]} ${utc.getUTCDate()} ${MONTHS[utc.getUTCMonth()]}` +
    ` ${pad(utc.getUTCHours())}:${pad(utc.getUTCMinutes())} UTC`;
  if (mode === "utc") return utcFull;
  // Both: the site's clock leads, UTC follows in brackets, and the UTC half
  // repeats the weekday only when the two fall on different days — "Wed 23:00
  // (Thu 04:00 UTC)" names the day boundary instead of hiding it.
  const dayDiffers = utc.getUTCDay() !== site.getUTCDay();
  const utcTxt =
    `${dayDiffers ? `${WEEKDAYS[utc.getUTCDay()]} ` : ""}` +
    `${pad(utc.getUTCHours())}:${pad(utc.getUTCMinutes())} UTC`;
  return `${siteTxt} (${utcTxt})`;
}

/** How the document names its own clock convention, on its face. */
export function clockNote(mode: ReportClock, utcOffsetSeconds: number): string {
  const h = utcOffsetSeconds / 3600;
  const off = `UTC${h >= 0 ? "+" : "−"}${Math.abs(h) % 1 === 0 ? Math.abs(h) : Math.abs(h).toFixed(1)}`;
  if (mode === "utc") return "Every hour in this report is UTC.";
  if (mode === "site") return `Every hour in this report is the site's local clock (${off}).`;
  return `Hours are the site's local clock (${off}) with UTC in brackets.`;
}

/**
 * The download's file name: reference + vessel + operation, slugged —
 * "SS-20260813-0420-brooks-mccall-cpt-push.html". The vessel's "R/V " prefix
 * and its "— 48.5 m" length suffix are trimmed before slugging: they are
 * catalogue dressing, not identity, and a filename is read in a directory
 * listing where every character costs.
 */
export function reportFileName(ref: string, vesselName: string, opLabel: string): string {
  const slug = (s: string): string =>
    s
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "") // é → e; a filename should survive any filesystem
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  const vessel = vesselName.replace(/ —.*$/, "").replace(/^r\/?v\.?\s+/i, "");
  return [ref, slug(vessel), slug(opLabel)].filter((s) => s !== "").join("-") + ".html";
}

// --- Window mapping ----------------------------------------------------------

export interface WindowIndices {
  /** First and last hour of the REQUESTED window, clamped into the forecast. */
  a: number;
  b: number;
  /** The charting span: the window plus context margin either side. */
  ca: number;
  cb: number;
  /** True when the requested start/end fell outside forecast coverage and was
   *  clamped — the document must say so rather than silently shrink the ask. */
  clampedStart: boolean;
  clampedEnd: boolean;
}

/**
 * Map a requested window (start instant + duration) onto the forecast's hourly
 * label axis. Returns null when the window misses the forecast entirely — the
 * caller shows a refusal, never a silently-empty document. A window that
 * merely overhangs an end is clamped and flagged.
 */
export function windowIndices(
  times: string[],
  utcOffsetSeconds: number,
  startMs: number,
  hours: number,
  marginH: number,
): WindowIndices | null {
  const n = times.length;
  if (n === 0 || hours <= 0 || !Number.isFinite(startMs)) return null;
  const epochs = times.map((t) => epochOfLabel(t, utcOffsetSeconds));
  const first = epochs[0];
  const last = epochs[n - 1];
  if (!Number.isFinite(first) || !Number.isFinite(last)) return null;

  const tol = HOUR_MS / 2;
  // The window covers `hours` whole hours; its last SAMPLE is hours−1 after
  // the first (half-open interval sampled hourly).
  const endMs = startMs + (hours - 1) * HOUR_MS;
  if (endMs < first - tol || startMs > last + tol) return null;

  const nearest = (t: number): number => {
    let best = 0;
    let bestDiff = Infinity;
    for (let i = 0; i < n; i++) {
      const diff = Math.abs(epochs[i] - t);
      if (diff < bestDiff) {
        bestDiff = diff;
        best = i;
      }
    }
    return best;
  };

  const a = nearest(Math.max(startMs, first));
  const b = Math.max(a, nearest(Math.min(endMs, last)));
  return {
    a,
    b,
    ca: Math.max(0, a - marginH),
    cb: Math.min(n - 1, b + marginH),
    clampedStart: startMs < first - tol,
    clampedEnd: endMs > last + tol,
  };
}

// --- Worst-hour verdict -------------------------------------------------------

export interface WorstHour {
  /** The lowest workability probability in [a, b]. */
  p: number;
  /** Its hour index. */
  index: number;
  /** How many hours in the window actually had a probability. */
  covered: number;
  total: number;
}

/**
 * The window's binding constraint. The verdict describes the WHOLE window
 * graded on its worst hour — the console's stated rule (a window that is Go
 * for eleven hours and No-go for one is not a Go; the crew is already on deck
 * when it breaks). Null when no hour in the window has data.
 */
export function worstHour(p: (number | null)[], a: number, b: number): WorstHour | null {
  let worst: number | null = null;
  let index = -1;
  let covered = 0;
  const lo = Math.max(0, a);
  const hi = Math.min(p.length - 1, b);
  for (let i = lo; i <= hi; i++) {
    const v = p[i];
    if (v === null || v === undefined) continue;
    covered++;
    if (worst === null || v < worst) {
      worst = v;
      index = i;
    }
  }
  if (worst === null) return null;
  return { p: worst, index, covered, total: Math.max(0, hi - lo + 1) };
}

// --- Weather windows near the requested span ---------------------------------

export interface NearWindow {
  start: number;
  end: number; // half-open, as workability() emits
  relation: "overlaps" | "before" | "after";
}

/**
 * The workability windows worth printing: any that overlap the requested span,
 * plus any that begin or end within `adjacentH` hours of it. The adjacent ones
 * are the report's useful bad news — "not in your window, but here is the one
 * next to it" — and hiding them would make a No-go read like a dead end.
 */
export function windowsNear(
  windows: { start: number; end: number }[],
  a: number,
  b: number,
  adjacentH: number,
): NearWindow[] {
  const out: NearWindow[] = [];
  for (const w of windows) {
    if (w.start <= b && w.end > a) out.push({ ...w, relation: "overlaps" });
    else if (w.end <= a && a - w.end < adjacentH) out.push({ ...w, relation: "before" });
    else if (w.start > b && w.start - b <= adjacentH) out.push({ ...w, relation: "after" });
  }
  return out;
}

// --- Spread measures over the window -----------------------------------------

/** Linear-interpolation quantile of an ascending-sorted, non-empty array. */
function quantile(sortedAsc: number[], q: number): number {
  const n = sortedAsc.length;
  if (n === 1) return sortedAsc[0];
  const pos = q * (n - 1);
  const lo = Math.floor(pos);
  const hi = Math.min(lo + 1, n - 1);
  return sortedAsc[lo] + (sortedAsc[hi] - sortedAsc[lo]) * (pos - lo);
}

/** Mean per-hour ensemble width (p95 − p05, kn) over [a, b], or null when no
 *  hour in the span has members. The briefing's own confidence measure,
 *  recomputed for the report's window. */
export function windowEnsWidth(
  members: (number[] | null)[] | null | undefined,
  a: number,
  b: number,
): number | null {
  if (!members) return null;
  let sum = 0;
  let count = 0;
  for (let i = Math.max(0, a); i <= b && i < members.length; i++) {
    const m = members[i];
    if (!m || m.length === 0) continue;
    const s = [...m].sort((x, y) => x - y);
    sum += quantile(s, 0.95) - quantile(s, 0.05);
    count++;
  }
  return count > 0 ? sum / count : null;
}

/** Mean per-hour spread (max − min) across several model series over [a, b].
 *  Multi-model disagreement between deterministic runs, NOT an ensemble — the
 *  prose must never call it one. Hours with fewer than two reporting models
 *  have no spread to measure and are skipped. */
export function windowModelSpread(
  series: ((number | null)[] | null | undefined)[],
  a: number,
  b: number,
): number | null {
  let sum = 0;
  let count = 0;
  for (let i = Math.max(0, a); i <= b; i++) {
    const v: number[] = [];
    for (const arr of series) {
      const x = arr?.[i];
      if (x !== null && x !== undefined && Number.isFinite(x)) v.push(x);
    }
    if (v.length >= 2) {
      sum += Math.max(...v) - Math.min(...v);
      count++;
    }
  }
  return count > 0 ? sum / count : null;
}

// --- Formal-register sentences ------------------------------------------------

/**
 * The confidence paragraph. Same thresholds as the window briefing (±4/±7.5 kn
 * half-widths for wind; ±0.25/±0.75 m for waves) so the report can never grade
 * the same spread differently than the console does. `waveSpreadDisplay` is
 * already in the display unit; `waveUnit` names it.
 */
export function confidenceStatement(
  ensWidthKn: number | null,
  waveSpreadDisplay: number | null,
  waveUnit: string,
  waveTightHalf: number,
  waveWideHalf: number,
): string {
  const parts: string[] = [];
  if (ensWidthKn !== null) {
    const read =
      ensWidthKn < 8
        ? "tight — the members agree and the forecast is well-constrained"
        : ensWidthKn < 15
          ? "moderate — plan with some slack"
          : "wide — the atmosphere has not settled; treat any single value as one scenario among several";
    parts.push(
      `ensemble wind spread averages ±${(ensWidthKn / 2).toFixed(0)} kn across the window (${read})`,
    );
  } else {
    parts.push(
      "the wind ensemble was unavailable for this window, so no member-spread confidence read is possible",
    );
  }
  if (waveSpreadDisplay !== null) {
    const half = waveSpreadDisplay / 2;
    const read =
      half <= waveTightHalf
        ? "tight — the wave models agree"
        : half < waveWideHalf
          ? "moderate"
          : "wide — the wave models disagree";
    parts.push(
      `wave-model spread averages ±${half.toFixed(1)} ${waveUnit} ` +
        `(${read}; disagreement between deterministic models, not an ensemble bound)`,
    );
  } else {
    parts.push("fewer than two wave models reported, so wave-model agreement cannot be measured");
  }
  return `Confidence: ${parts.join("; ")}.`;
}

/**
 * The forecast-horizon honesty paragraph, with the one citation this module
 * carries. The numbers are quoted, not derived: Campos et al. 2024 (Weather
 * and Forecasting 39(11), NOAA's 20-year global wave-ensemble reforecast
 * against 98 deep-water buoys) reports significant-wave-height correlation
 * above 0.9 through day 4 and above 0.8 through day 7, and the WMO
 * verification intercomparison shows tropical-belt skill degrading least with
 * lead — which is TDI's water. Do not touch these figures without reading the
 * sourced notes they came from.
 */
export function skillStatement(leadStartH: number, leadEndH: number): string {
  const s = Math.max(0, Math.round(leadStartH));
  const e = Math.max(s, Math.round(leadEndH));
  const span =
    s === 0
      ? `runs from the forecast's current hour to ${e} hours out`
      : `sits ${s}–${e} hours beyond the forecast's current hour`;
  let posture: string;
  if (e <= 72) {
    posture = "The whole window lies inside the planning-grade range (days 1–3).";
  } else if (s >= 96) {
    posture =
      "The whole window lies beyond day 4 — read the verdict as a trend and a range of outcomes, " +
      "not a firm value for a given hour, and re-issue this report as fresher model cycles arrive.";
  } else {
    posture =
      "The window crosses from planning-grade lead into the range where values soften — " +
      "weight its early hours more heavily, and re-issue this report as fresher model cycles arrive.";
  }
  return (
    `Forecast horizon: this window ${span}. Skill decays with lead time. ` +
    `Verification of NOAA's global wave-ensemble reforecast holds significant-wave-height ` +
    `correlation above 0.9 through day 4 and above 0.8 through day 7 in deep water ` +
    `(Campos et al. 2024, Weather and Forecasting), and tropical-belt verification degrades ` +
    `least with lead time. ${posture}`
  );
}
